import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * Text clamped to N lines with an in-place Show more / Show less toggle.
 *
 * A bare `line-clamp-*` silently eats the tail of anything long — which is how
 * workflow AI summaries reached the notification bell as "…was added by user
 * \"88c2a503-…". The toggle only renders when the text actually overflows, so
 * short bodies look exactly as they did before.
 *
 * The toggle stops click propagation unconditionally: these live inside
 * clickable cards (notification rows navigate on click), and expanding text
 * must never double as activating the row.
 *
 * Not to be confused with `features/inbox/.../ExpandableText`, which collapses
 * to a pixel height rather than a line count — the wrong unit for a two-line
 * list row.
 */

/** Tailwind needs whole class names at build time — no `line-clamp-${n}`. */
const CLAMP_CLASS = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
    5: 'line-clamp-5',
    6: 'line-clamp-6',
} as const;

interface ClampedTextProps {
    readonly text: string;
    /** How many lines to show while collapsed. */
    readonly lines?: keyof typeof CLAMP_CLASS;
    readonly className?: string;
    readonly toggleClassName?: string;
}

export function ClampedText({ text, lines = 2, className, toggleClassName }: ClampedTextProps) {
    const { t } = useTranslation();
    const bodyRef = useRef<HTMLParagraphElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);

    // Measure only while collapsed — expanded, scrollHeight === clientHeight
    // and the toggle would disappear the moment it was used.
    useLayoutEffect(() => {
        const el = bodyRef.current;
        if (!el || isExpanded) return;
        setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }, [text, isExpanded, lines]);

    return (
        <div className="flex flex-col gap-0.5">
            <p
                ref={bodyRef}
                className={cn(
                    'whitespace-pre-line wrap-break-word',
                    !isExpanded && CLAMP_CLASS[lines],
                    className,
                )}
            >
                {text}
            </p>
            {(isClamped || isExpanded) && (
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded((prev) => !prev);
                    }}
                    className={cn(
                        'self-start text-xs font-medium text-primary-600 hover:underline dark:text-primary-400',
                        toggleClassName,
                    )}
                >
                    {isExpanded
                        ? t('common.showLess', 'Show less')
                        : t('common.showMore', 'Show more')}
                </button>
            )}
        </div>
    );
}
